import type { JourneyCoreState, JourneySpec, JourneyStep } from '../../journey'

export type ChileautosHighlightsView = 'home' | 'loading' | 'users' | 'permissions' | 'publish'

/** The numbered step the story column lights up; null between loops. */
export type ChileautosHighlightsStep = 1 | 2 | 3 | null

export interface ChileautosHighlightsState extends JourneyCoreState {
  view: ChileautosHighlightsView
  navActive: 'inicio' | 'publicaciones' | 'usuarios'
  /** Act 1 is the administrator's session; acts 2 and 3 the seller's. */
  seller: boolean
  /** The seller signed in again: roles travel in the session token. */
  relogged: boolean
  step: ChileautosHighlightsStep
  permissionHighlighted: boolean
  permissionChecked: boolean
  /** The label's hover hint. Desktop only: a phone has no hover. */
  hintVisible: boolean
  toast: string | null
  editorOpen: boolean
  chaTab: boolean
  destacadosTab: boolean
  confirmOpen: boolean
  applied: boolean
  filtersOpen: boolean
  menu: 'platform' | 'highlight' | null
  hoveredOption: 'chileautos' | 'topspot' | null
  platformChosen: boolean
  highlightChosen: boolean
  /** Phone only: what the long screens are scrolled to. */
  permissionsPan: string | null
  publishPan: string | null
}

const INITIAL: ChileautosHighlightsState = {
  view: 'home',
  caption: 'Todo comienza en Inicio',
  target: null,
  cursorVisible: true,
  clicking: false,
  clickSeq: 0,
  navActive: 'inicio',
  seller: false,
  relogged: false,
  step: null,
  permissionHighlighted: false,
  permissionChecked: false,
  hintVisible: false,
  toast: null,
  editorOpen: false,
  chaTab: false,
  destacadosTab: false,
  confirmOpen: false,
  applied: false,
  filtersOpen: false,
  menu: null,
  hoveredOption: null,
  platformChosen: false,
  highlightChosen: false,
  permissionsPan: null,
  publishPan: null,
}

/*
El permiso va primero, a pedido de los stakeholders: la primera versión
empezaba aplicando el destacado y dejaba el permiso como una línea al costado,
y quien la viera iba a buscar la pestaña sin tenerlo. Nadie lo tiene por
defecto, así que el recorrido lo muestra antes que nada.

Tres actos con el mismo texto en escritorio y en teléfono: el administrador da
el permiso, el vendedor vuelve a iniciar sesión y aplica un TopSpot, y el
filtro nuevo lo encuentra.
*/
const SESSION_SWAP: Partial<ChileautosHighlightsState> = {
  toast: null,
  seller: true,
  relogged: true,
  view: 'home',
  navActive: 'inicio',
  target: null,
  step: 2,
  caption: 'Camilo vuelve a iniciar sesión y ya puede destacar',
}

const APPLIED: Partial<ChileautosHighlightsState> = {
  applied: true,
  toast: 'Destacado aplicado en ChileAutos',
  caption: 'TopSpot aplicado, sin salir de Veekls',
}

// Escritorio: el cursor viaja hasta cada destino y hace clic al llegar.
const buildDesktop = (): JourneyStep<ChileautosHighlightsState>[] => [
  // Acto 1 — el administrador da el permiso
  { at: 500, patch: { step: 1 } },
  { at: 700, patch: { target: 'navUsuarios' } },
  { at: 1700, click: true, patch: { navActive: 'usuarios', caption: 'Primero, el permiso: entramos a Usuarios' } },
  { at: 2000, patch: { view: 'loading' } },
  { at: 2600, patch: { view: 'users' } },
  { at: 3000, patch: { target: 'member' } },
  { at: 3900, click: true, patch: { caption: 'Abrimos el perfil de un vendedor' } },
  { at: 4200, patch: { view: 'loading' } },
  {
    at: 4800,
    patch: {
      view: 'permissions',
      permissionHighlighted: true,
      caption: 'El permiso nuevo, junto a Publicar en plataformas externas',
    },
  },
  { at: 5400, patch: { target: 'permissionLabel' } },
  { at: 6200, patch: { hintVisible: true, caption: 'Nadie tiene el permiso por defecto: usa cupos pagados' } },
  { at: 7600, patch: { hintVisible: false, target: 'permissionBox' } },
  {
    at: 8300,
    click: true,
    patch: {
      permissionChecked: true,
      permissionHighlighted: false,
      caption: 'Lo marcamos para quien podrá destacar',
    },
  },
  { at: 8900, patch: { target: 'save' } },
  { at: 9800, click: true, patch: { toast: 'Permisos actualizados', caption: 'Grabamos los cambios' } },

  // Cambio de sesión
  { at: 11200, patch: SESSION_SWAP },

  // Acto 2 — el vendedor destaca un aviso
  { at: 12600, patch: { target: 'navPublicaciones' } },
  { at: 13500, click: true, patch: { navActive: 'publicaciones', caption: 'Entramos a Publicaciones' } },
  { at: 13800, patch: { view: 'loading' } },
  { at: 14400, patch: { view: 'publish', relogged: false } },
  { at: 14800, patch: { target: 'editRow' } },
  { at: 15600, click: true, patch: { editorOpen: true, caption: 'Abrimos un aviso, el del FIAT 500X' } },
  { at: 16000, patch: { target: 'chaTab' } },
  { at: 16600, click: true, patch: { chaTab: true } },
  { at: 17000, patch: { target: 'destacadosTab' } },
  {
    at: 17600,
    click: true,
    patch: { destacadosTab: true, caption: 'La pestaña Destacados, con tu saldo a la vista' },
  },
  { at: 18200, patch: { target: 'apply' } },
  {
    at: 19100,
    click: true,
    patch: { confirmOpen: true, caption: 'Antes de gastar un cupo, Veekls pide confirmación' },
  },
  { at: 20300, patch: { target: 'confirmYes' } },
  { at: 21100, click: true, patch: { confirmOpen: false } },
  { at: 21400, patch: APPLIED },

  // Acto 3 — encontrarlo con el filtro
  { at: 23000, patch: { toast: null, target: 'editRow' } },
  { at: 23700, click: true, patch: { editorOpen: false } },
  { at: 24100, patch: { target: 'filtersToggle' } },
  {
    at: 24800,
    click: true,
    patch: { filtersOpen: true, step: 3, caption: 'Ahora, a encontrar tus destacados' },
  },
  { at: 25300, patch: { target: 'platformSelect' } },
  { at: 25900, click: true, patch: { menu: 'platform' } },
  { at: 26200, patch: { target: 'optionChileautos' } },
  { at: 26500, patch: { hoveredOption: 'chileautos' } },
  {
    at: 27000,
    click: true,
    patch: {
      menu: null,
      hoveredOption: null,
      platformChosen: true,
      caption: 'Con ChileAutos aparece el filtro Destacado',
    },
  },
  { at: 27700, patch: { target: 'highlightSelect' } },
  { at: 28300, click: true, patch: { menu: 'highlight' } },
  { at: 28700, patch: { target: 'optionTopspot' } },
  { at: 29000, patch: { hoveredOption: 'topspot' } },
  {
    at: 29500,
    click: true,
    patch: { menu: null, hoveredOption: null, highlightChosen: true, caption: 'Solo los avisos con TopSpot' },
  },
  { at: 31000, patch: { step: null, caption: 'Permiso, destacado y filtro: listo' } },
]

/*
Teléfono: un dedo no viaja, toca. Cada toque nombra su destino en el mismo paso
y el escenario mide ahí dónde cae. Las pantallas largas (los permisos, el aviso
abierto) se deslizan antes del toque, como lo haría el pulgar.
*/
const buildPhone = (): JourneyStep<ChileautosHighlightsState>[] => [
  // Acto 1 — el administrador da el permiso
  { at: 500, patch: { step: 1 } },
  {
    at: 1000,
    click: true,
    patch: { target: 'navUsuarios', navActive: 'usuarios', caption: 'Primero, el permiso: entramos a Usuarios' },
  },
  { at: 1300, patch: { view: 'loading' } },
  { at: 1900, patch: { view: 'users' } },
  { at: 2600, click: true, patch: { target: 'member', caption: 'Abrimos el perfil de un vendedor' } },
  { at: 2900, patch: { view: 'loading' } },
  { at: 3500, patch: { view: 'permissions', caption: 'Bajamos hasta los permisos' } },
  { at: 4100, patch: { permissionsPan: 'permissionBox' } },
  {
    at: 4800,
    patch: { permissionHighlighted: true, caption: 'El permiso nuevo, junto a Publicar en plataformas externas' },
  },
  { at: 6300, patch: { caption: 'Nadie tiene el permiso por defecto: usa cupos pagados' } },
  {
    at: 7600,
    click: true,
    patch: {
      target: 'permissionBox',
      permissionChecked: true,
      permissionHighlighted: false,
      caption: 'Lo marcamos para quien podrá destacar',
    },
  },
  { at: 8400, patch: { permissionsPan: 'save' } },
  {
    at: 9200,
    click: true,
    patch: { target: 'save', toast: 'Permisos actualizados', caption: 'Grabamos los cambios' },
  },

  // Cambio de sesión
  { at: 10400, patch: SESSION_SWAP },

  // Acto 2 — el vendedor destaca un aviso
  {
    at: 11900,
    click: true,
    patch: {
      target: 'navPublicaciones',
      permissionsPan: null,
      navActive: 'publicaciones',
      caption: 'Entramos a Publicaciones',
    },
  },
  { at: 12200, patch: { view: 'loading' } },
  { at: 12600, patch: { relogged: false } },
  { at: 12800, patch: { view: 'publish' } },
  {
    at: 13500,
    click: true,
    patch: { target: 'editRow', editorOpen: true, caption: 'Abrimos un aviso, el del FIAT 500X' },
  },
  { at: 14300, click: true, patch: { target: 'chaTab', chaTab: true } },
  {
    at: 15000,
    click: true,
    patch: {
      target: 'destacadosTab',
      destacadosTab: true,
      caption: 'La pestaña Destacados, con tu saldo a la vista',
    },
  },
  { at: 15600, patch: { publishPan: 'apply' } },
  {
    at: 16600,
    click: true,
    patch: { target: 'apply', confirmOpen: true, caption: 'Antes de gastar un cupo, Veekls pide confirmación' },
  },
  { at: 18100, click: true, patch: { target: 'confirmYes', confirmOpen: false } },
  { at: 18400, patch: APPLIED },

  // Acto 3 — encontrarlo con el filtro
  { at: 20200, patch: { toast: null, publishPan: null } },
  { at: 20900, click: true, patch: { target: 'editRow', editorOpen: false } },
  {
    at: 21600,
    click: true,
    patch: { target: 'filtersToggle', filtersOpen: true, step: 3, caption: 'Ahora, a encontrar tus destacados' },
  },
  { at: 22400, click: true, patch: { target: 'platformSelect', menu: 'platform' } },
  { at: 23000, patch: { hoveredOption: 'chileautos' } },
  {
    at: 23300,
    click: true,
    patch: {
      target: 'optionChileautos',
      menu: null,
      hoveredOption: null,
      platformChosen: true,
      caption: 'Con ChileAutos aparece el filtro Destacado',
    },
  },
  { at: 24500, click: true, patch: { target: 'highlightSelect', menu: 'highlight' } },
  { at: 25100, patch: { hoveredOption: 'topspot' } },
  {
    at: 25400,
    click: true,
    patch: {
      target: 'optionTopspot',
      menu: null,
      hoveredOption: null,
      highlightChosen: true,
      caption: 'Solo los avisos con TopSpot',
    },
  },
  { at: 27500, patch: { step: null, caption: 'Permiso, destacado y filtro: listo' } },
]

export const chileautosHighlightsSpec: JourneySpec<ChileautosHighlightsState> = {
  initial: INITIAL,
  loopMs: 34_000,
  build: buildDesktop,
}

export const chileautosHighlightsPhoneSpec: JourneySpec<ChileautosHighlightsState> = {
  initial: INITIAL,
  loopMs: 30_500,
  build: buildPhone,
}
